import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import Cookies from 'js-cookie'

import { useApiStore } from '@/stores/app/api'
import { useUserStore } from '@/stores/app/user'
import { api, cookies } from '@/utils/constants'
import { pathWithId } from '@/utils/router'

import { Product } from '@/types/catalogue/product'
import { ArticleCart, StepOrderCreateKonvives } from '@/types/konvives/order'
import { ArticleOrder, PayloadOrder } from '@/types/operations/order'
import { Site } from '@/types/park/site'

const DEFAULT_STEP = StepOrderCreateKonvives.enum.PRODUCTS

type useOrderCreateStore = {
    /** Know the current step of the order creation. */
    step: StepOrderCreateKonvives

    phoneNumber: string

    siteSelected: Site | null

    articles: ArticleOrder[]

    canDisplayCartSidebar: boolean

    initialiseOrderCreate: () => Promise<void>

    updateStep: (newStep: StepOrderCreateKonvives) => void

    updatePhoneNumber: (newPhoneNumber: string) => void

    updateSiteSelected: (newSiteSelected: Site) => void

    updateArticles: (newArticles: ArticleOrder[]) => void

    updateCanDisplayCartSidebar: (newCanDisplayCartSidebar: boolean) => void

    /** Add an article to the list. */
    addArticle: (
        product: Product,
        isWeightQuantity?: boolean,
        quantity?: number,
    ) => void

    createOrder: () => Promise<boolean>

    /** Reset the order creation data. */
    resetOrderCreate: () => void
}

export const useOrderCreateStore = create<useOrderCreateStore>()(
    immer((set, get) => ({
        step: DEFAULT_STEP,

        konviveSelected: null,

        phoneNumber: '',

        siteSelected: null,

        articles: [],

        canDisplayCartSidebar: false,

        initialiseOrderCreate: async () => {
            const { request, requestWithoutHooks } = useApiStore.getState()
            const { user } = useUserStore.getState()

            const requestInstance =
                user.id === -1 ? requestWithoutHooks : request

            const articlesFormatted: ArticleOrder[] = []

            let articlesCookies = Cookies.get(cookies.cart.CART)

            try {
                articlesCookies = JSON.parse(articlesCookies)
            } catch {
                /* Empty */
            }

            const articlesCookiesParsed =
                ArticleCart.array().safeParse(articlesCookies)

            if (!articlesCookiesParsed.success) {
                return
            }

            for (const article of articlesCookiesParsed.data) {
                // eslint-disable-next-line no-await-in-loop
                const requestProduct = await requestInstance
                    .get(
                        pathWithId(
                            api.catalogue.products.DETAILS,
                            article.product,
                        ),
                    )
                    .json<Product>()
                    .catch(() => {
                        /* Empty */
                    })

                if (requestProduct) {
                    articlesFormatted.push({
                        ...article,
                        product: requestProduct,
                    })
                }
            }

            set((state) => {
                state.articles = articlesFormatted
            })
        },

        updateStep: (newStep) => {
            set((state) => {
                state.step = newStep
            })
        },

        updatePhoneNumber: (newPhoneNumber) => {
            set((state) => {
                state.phoneNumber = newPhoneNumber
            })
        },

        updateSiteSelected: (newSiteSelected) => {
            set((state) => {
                state.siteSelected = newSiteSelected
            })
        },

        updateArticles: (newArticles) => {
            set((state) => {
                state.articles = newArticles
            })

            const articlesFormatted: ArticleCart[] = newArticles.map(
                (article) => ({
                    ...article,
                    product: article.product.id,
                }),
            )

            Cookies.set(
                cookies.cart.CART,
                JSON.stringify(articlesFormatted),
                cookies.cart.OPTIONS,
            )
        },

        updateCanDisplayCartSidebar: (newCanDisplayCartSidebar) => {
            set((state) => {
                state.canDisplayCartSidebar = newCanDisplayCartSidebar
            })
        },

        addArticle: (product, isWeightQuantity, quantity) => {
            const { articles, updateArticles } = get()

            const articlesCloned = structuredClone(articles)

            const articleFound = articlesCloned.find(
                (article) => article.product.id === product.id,
            )

            // Already added
            if (articleFound) {
                articleFound.quantity += articleFound.is_weight_quantity
                    ? product.weight
                    : 1
            }

            // Not already added
            if (!articleFound) {
                articlesCloned.push({
                    product,
                    is_weight_quantity:
                        isWeightQuantity === undefined
                            ? product.is_price_weight
                            : isWeightQuantity,
                    quantity:
                        quantity ??
                        (product.is_price_weight ? product.weight : 1),
                })

                articlesCloned.sort((previous, current) =>
                    previous.product.name.localeCompare(current.product.name),
                )
            }

            updateArticles(articlesCloned)
        },

        // TODO: Remove boolean when adding toast
        createOrder: async () => {
            const {
                articles,
                phoneNumber,
                resetOrderCreate,
                siteSelected,
                updateArticles,
            } = get()

            const { request, requestWithoutHooks } = useApiStore.getState()
            const { user } = useUserStore.getState()

            const requestInstance =
                user.id === -1 ? requestWithoutHooks : request

            if (!siteSelected || !articles) {
                return false
            }

            const dataFormated: PayloadOrder = {
                site: siteSelected.id,
                articles: articles.map((article) => ({
                    ...article,
                    product: article.product.id,
                })),
                ...(user.id > -1 && {
                    konvive: user.id,
                }),
                ...(user.id === -1 && {
                    phone_number: phoneNumber,
                }),
            }

            const requestCreateOrder = await requestInstance
                .post(api.operations.orders.LIST, {
                    json: dataFormated,
                })
                .catch(() => {
                    /* Empty */
                })

            if (requestCreateOrder) {
                updateArticles([])
                resetOrderCreate()
            }

            return !!requestCreateOrder
        },

        resetOrderCreate: () => {
            set((state) => {
                state.step = DEFAULT_STEP
                state.siteSelected = null
                state.phoneNumber = null
            })
        },
    })),
)
